import { supabase } from '../lib/supabase';
import { extractDetailedComponentsFromModel } from '../utils/gltfParser';

// Convert snake_case to camelCase
const toCamelCase = (obj) => {
  if (!obj) return obj;
  
  return {
    id: obj?.id,
    name: obj?.name,
    slug: obj?.slug,
    sku: obj?.sku,
    description: obj?.description,
    price: obj?.price,
    stockQuantity: obj?.stock_quantity,
    categoryId: obj?.category_id,
    categoryName: obj?.category?.name,
    categorySlug: obj?.category?.slug,
    manufacturerId: obj?.manufacturer_id,
    manufacturerName: obj?.manufacturer?.name || obj?.manufacturer_name,
    manufacturerSlug: obj?.manufacturer?.slug,
    isFeatured: obj?.is_featured,
    isCertified: obj?.is_certified,
    staffpick: obj?.staffpick,
    thumbnailPath: obj?.thumbnail_path,
    thumbnailUrl: obj?.thumbnailUrl,
    imageUrls: obj?.image_urls,
    gltfFilePath: obj?.gltf_file_path,
    componentCount: obj?.component_count || 0,
    modelUrl: obj?.modelUrl,
    is3dManufacturing: obj?.is_3d_manufacturing,
    preferred3dMethod: obj?.preferred_3d_method,
    isCncCapable: obj?.is_cnc_capable,
    cncMaterial: obj?.cnc_material,
    cncGrade: obj?.cnc_grade,
    soldCount: Number(obj?.sold_count) || 0,
    sold_count: Number(obj?.sold_count) || 0,
    createdAt: obj?.created_at,
    updatedAt: obj?.updated_at,
    createdBy: obj?.created_by,
    averageRating: typeof obj?.average_rating === 'number' ? obj?.average_rating : 0,
    totalRatings: typeof obj?.total_ratings === 'number' ? obj?.total_ratings : 0,
    componentCount: typeof obj?.component_count === 'number' ? obj?.component_count : 0,
    addedCount: typeof obj?.added_count === 'number' ? obj?.added_count : 0,
    upc: obj?.upc,
    specs: obj?.specs || {},
    sellProductOnly: obj?.sell_product_only, // NEW: Sell product assembly only flag
    sell_product_only: obj?.sell_product_only, // NEW: Symmetrical snake_case fallback
    vinSn: obj?.vin_sn,
    sellerBusinessName: obj?.sellerBusinessName,
    isSellerVerified: obj?.isSellerVerified,
    sellerRating: obj?.sellerRating,
    sellerBusinessSlug: obj?.sellerBusinessSlug,
    isBoosted: obj?.is_boosted || false,
    seasonalSetting: obj?.seasonal_setting || '',
    holidaySetting: obj?.holiday_setting || '',
    viewsCount: Number(obj?.views_count) || 0,
    views_count: Number(obj?.views_count) || 0,
    combinedViews: Number(obj?.combined_views) || Number(obj?.views_count) || 0,
  };
};

const toSnakeCase = (obj) => {
  return {
    sku: obj?.sku || null,
    name: obj?.name,
    slug: obj?.slug || null,
    description: obj?.description,
    price: obj?.price,
    stock_quantity: obj?.stockQuantity || 0,
    category_id: obj?.categoryId === '' ? null : obj?.categoryId,
    manufacturer_id: obj?.manufacturerId === '' ? null : obj?.manufacturerId,
    thumbnail_path: obj?.thumbnailPath || null,
    gltf_file_path: obj?.gltfFilePath || null,
    is_featured: obj?.isFeatured || false,
    is_certified: obj?.isCertified || false,
    staffpick: obj?.staffpick || false,
    image_urls: obj?.imageUrls || [],
    is_3d_manufacturing: Boolean(obj?.is3dManufacturing),
    preferred_3d_method: obj?.is3dManufacturing ? (obj?.preferred3dMethod || null) : null,
    is_cnc_capable: Boolean(obj?.isCncCapable),
    cnc_material: obj?.isCncCapable ? (obj?.cncMaterial || null) : null,
    cnc_grade: obj?.isCncCapable ? (obj?.cncGrade || null) : null,
    component_count: obj?.componentCount || 0,
    upc: obj?.upc || null,
    vin_sn: obj?.vinSn || null,
    keywords: obj?.keywords || '',
    seasonal_setting: obj?.seasonalSetting || obj?.seasonal_setting || '',
    holiday_setting: obj?.holidaySetting || obj?.holiday_setting || '',
    status: obj?.status || 'active'
  };
};

export const productService = {
  async getAll(filters = {}) {
    try {
      const buildQuery = (isFallback = false) => {
        let query = supabase?.from('products')?.select(`
            *,
            component_count,
            upc,
        added_count,
        specs,
        specs,
        specs,
            category:categories(id, name, slug, description),
            manufacturer:manufacturers(id, name, slug, logo_url)
          `, { count: 'exact' });

        if (filters?.includeInactive !== true) {
          query = query?.eq('status', 'active');
        }

        if (filters?.categoryIds?.length > 0) {
          query = query?.in('category_id', filters?.categoryIds);
        }

        if (filters?.manufacturerIds?.length > 0) {
          query = query?.in('manufacturer_id', filters?.manufacturerIds);
        }

        if (filters?.priceMin !== undefined) {
          query = query?.gte('price', filters?.priceMin);
        }
        if (filters?.priceMax !== undefined) {
          query = query?.lte('price', filters?.priceMax);
        }

        if (filters?.isFeatured !== undefined) {
          query = query?.eq('is_featured', filters?.isFeatured);
        }

        if (filters?.holiday) {
          const search = `"%${String(filters.holiday).replace(/"/g, '""')}%"`;
          query = query?.or(`holiday_setting.ilike.${search},keywords.ilike.${search},name.ilike.${search}`);
        }

        if (filters?.season) {
          const search = `"%${String(filters.season).replace(/"/g, '""')}%"`;
          query = query?.or(`seasonal_setting.ilike.${search},keywords.ilike.${search},name.ilike.${search}`);
        }

        if (filters?.searchTerm) {
          const words = String(filters.searchTerm).toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
          if (isFallback) {
              const orConditions = words.map(word => {
                 const search = `"%${word.replace(/"/g, '""')}%"`;
                 return `name.ilike.${search},description.ilike.${search},upc.ilike.${search},sku.ilike.${search}`;
              }).join(',');
              query = query.or(orConditions);
          } else {
              for (const word of words) {
                 const search = `"%${word.replace(/"/g, '""')}%"`;
                 query = query.or(`name.ilike.${search},description.ilike.${search},upc.ilike.${search},sku.ilike.${search}`);
              }
          }
        }
        return query;
      };

      let query = buildQuery(false);

      if (filters?.createdBy) {
        query = query?.eq('created_by', filters?.createdBy);
      }

      let boostedProducts = [];
      let excludeIds = [];
      
      const isPageOne = (!filters?.page || filters.page == 1);
      const isDefaultSort = (!filters?.sortBy || filters.sortBy === 'relevance' || filters.sortBy === 'newest');
      const hasCatPrefs = filters?.preferredCategoryNames?.length > 0;
      const hasBrandPrefs = filters?.preferredBrandIds?.length > 0;
      const hasSearch = !!filters?.searchTerm;

      if ((hasCatPrefs || hasBrandPrefs) && isPageOne && isDefaultSort && !hasSearch) {
        let preferredCategoryIds = [];
        if (hasCatPrefs) {
            const { data: allCats } = await supabase.from('categories').select('id, name');
            if (allCats) {
                const normalizedPrefs = filters.preferredCategoryNames.map(p => p.toLowerCase().trim());
                preferredCategoryIds = allCats.filter(c => normalizedPrefs.includes(c.name.toLowerCase().trim())).map(c => c.id);
            }
        }

        const candidateLimit = 40;
        let orConditions = [];
        if (preferredCategoryIds.length > 0) orConditions.push(`category_id.in.(${preferredCategoryIds.join(',')})`);
        if (hasBrandPrefs) orConditions.push(`manufacturer_id.in.(${filters.preferredBrandIds.join(',')})`);
        
        if (orConditions.length > 0) {
            const { data: candidateData } = await supabase
                .from('products')
                .select(`
                    *,
                    component_count,
                    upc,
        added_count,
        specs,
        specs,
        specs,
                    category:categories(id, name, slug, description),
                    manufacturer:manufacturers(id, name, slug, logo_url)
                `)
                .eq('status', 'active')
                .or(orConditions.join(','))
                .limit(candidateLimit)
                .order('created_at', { ascending: false });
                
            if (candidateData && candidateData.length > 0) {
                 const featured = candidateData.filter(p => p.is_featured);
                 const regular = candidateData.filter(p => !p.is_featured);
                 const shuffle = (array) => {
                     for (let i = array.length - 1; i > 0; i--) {
                         const j = Math.floor(Math.random() * (i + 1));
                         [array[i], array[j]] = [array[j], array[i]];
                     }
                     return array;
                 };
                 const finalBoosted = [...shuffle([...featured]), ...shuffle([...regular])].slice(0, 12);
                 boostedProducts = finalBoosted.map(p => ({ ...p, is_boosted: true }));
                 excludeIds = finalBoosted.map(p => p.id);
                 query = query.not('id', 'in', `(${excludeIds.join(',')})`);
            }
        }
      }

      const applySortingAndPagination = (q) => {
        switch (filters?.sortBy) {
          case 'price-low': q = q?.order('price', { ascending: true }); break;
          case 'price-high': q = q?.order('price', { ascending: false }); break;
          case 'name-asc': q = q?.order('name', { ascending: true }); break;
          case 'name-desc': q = q?.order('name', { ascending: false }); break;
          case 'rating': q = q?.order('average_rating', { ascending: false, nullsLast: true }); break;
          case 'oldest': q = q?.order('created_at', { ascending: true }); break;
          case 'popularity': q = q?.order('sold_count', { ascending: false, nullsFirst: false })?.order('views_count', { ascending: false, nullsFirst: false }); break;
          default: q = q?.order('created_at', { ascending: false });
        }
        const page = filters?.page || 1;
        const itemsPerPage = filters?.itemsPerPage || 24;
        const effectiveItemsPerPage = itemsPerPage - boostedProducts.length;
        const start = (page - 1) * itemsPerPage;
        const end = start + effectiveItemsPerPage - 1;
        return q?.range(start, end);
      };

      query = applySortingAndPagination(query);
      let { data, error, count } = await query;
      
      let isPartialMatch = false;
      const words = String(filters.searchTerm || '').trim().split(/\s+/).filter(w => w.length > 0);
      if (count === 0 && words.length > 1) {
          let fallbackQuery = buildQuery(true);
          if (excludeIds.length > 0) fallbackQuery = fallbackQuery.not('id', 'in', `(${excludeIds.join(',')})`);
          fallbackQuery = applySortingAndPagination(fallbackQuery);
          const fallbackResult = await fallbackQuery;
          data = fallbackResult.data;
          error = fallbackResult.error;
          count = fallbackResult.count;
          isPartialMatch = true;
      }

      if (error) throw new Error(error.message);
      
      let combinedData = data;
      if (boostedProducts.length > 0) combinedData = [...boostedProducts, ...data];

      const productsWithUrls = combinedData?.map(product => {
        if (product?.gltf_file_path) {
          const { data: urlData } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(product?.gltf_file_path);
          if (urlData?.publicUrl) product.modelUrl = urlData?.publicUrl;
        }
        if (product?.thumbnail_path) {
          if (product.thumbnail_path.startsWith('http')) {
            product.thumbnailUrl = product.thumbnail_path;
          } else {
            const { data: urlData } = supabase?.storage?.from('product-thumbnails')?.getPublicUrl(product?.thumbnail_path);
            if (urlData?.publicUrl) product.thumbnailUrl = urlData?.publicUrl;
          }
        }
        return product;
      }) || [];

      // Symmetrical Dynamic Views Aggregation:
      // Fetch views_count from seller_products and sum them up for each product dynamically!
      const productIds = productsWithUrls.map(p => p.id).filter(Boolean);
      let sellerViewsMap = {};
      try {
        if (productIds.length > 0) {
          const { data: sellerProds } = await supabase
            .from('seller_products')
            .select('product_id, views_count')
            .in('product_id', productIds)
            .or('isdeleted.is.null,isdeleted.eq.false');

          if (sellerProds) {
            sellerProds.forEach(sp => {
              const pId = sp.product_id;
              const views = Number(sp.views_count) || 0;
              sellerViewsMap[pId] = (sellerViewsMap[pId] || 0) + views;
            });
          }
        }
      } catch (e) {
        console.error('Error fetching combined seller product views in getAll:', e);
      }

      const products = productsWithUrls?.map(product => {
        let p = toCamelCase(product);
        const sellerViews = sellerViewsMap[p.id] || 0;
        // Total Views = product views_count + sum(seller_products views_count)
        p.combinedViews = (Number(product.views_count) || 0) + sellerViews;
        if (isPartialMatch) p.isPartialMatch = true;
        return p;
      }) || [];

      const pageVal = filters?.page || 1;
      const itemsPerPageVal = filters?.itemsPerPage || 24;

      return {
        products,
        totalCount: count || 0,
        page: pageVal,
        itemsPerPage: itemsPerPageVal,
        totalPages: Math.ceil((count || 0) / itemsPerPageVal)
      };
    } catch (error) {
      throw error;
    }
  },

  async create(product) {
    const productData = toSnakeCase(product);
    productData.created_by = product.createdBy;

    const { data, error } = await supabase?.from('products')?.insert(productData)?.select(`
        *,
        categories!products_category_id_fkey (id, name, slug)
      `)?.single();
    if (error) throw error;

    let updatedProductData = { ...data };
    if (updatedProductData?.gltf_file_path) {
      try {
        const { data: urlData } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(updatedProductData?.gltf_file_path);
        if (urlData?.publicUrl) {
          const components = await extractDetailedComponentsFromModel(urlData?.publicUrl);
          const componentCount = components?.length || 0;
          const { data: updateRes, error: updateError } = await supabase?.from('products')?.update({ component_count: componentCount })?.eq('id', updatedProductData?.id)?.select()?.single();
          if (!updateError) updatedProductData = { ...updatedProductData, ...updateRes };
        }
      } catch (componentError) {}
    }
    const productWithCategory = { ...updatedProductData, category_name: updatedProductData?.categories?.name || null };
    return toCamelCase(productWithCategory);
  },

  async update(id, product) {
    let productData = toSnakeCase(product);
    if (product?.gltfFilePath) {
      try {
        const { data: urlData } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(product?.gltfFilePath);
        if (urlData?.publicUrl) {
          const components = await extractDetailedComponentsFromModel(urlData?.publicUrl);
          productData.component_count = components?.length || 0;
        }
      } catch (componentError) {}
    }
    const { data, error } = await supabase?.from('products')?.update({ ...productData, upc: product?.upc || null })?.eq('id', id)?.select(`
        *,
        categories!products_category_id_fkey (id, name, slug)
      `)?.single();
    if (error) throw error;
    const productWithCategory = { ...data, category_name: data?.categories?.name || null };
    return toCamelCase(productWithCategory);
  },

  async delete(id) {
    const { error } = await supabase?.from('products')?.delete()?.eq('id', id);
    if (error) throw error;
  },

  async getById(id) {
    // Increment view count asynchronously
    supabase?.rpc('increment_product_views', { product_id: id })?.then(undefined, err => {
      console.error('Error incrementing product views:', err);
    });

    const { data: productDataFromDb, error: productError } = await supabase?.from('products')?.select(`
        *, 
        component_count,
        upc,
        added_count,
        specs,
        specs,
        specs,
        category:categories(id, name, slug),
        manufacturer:manufacturers(id, name, slug)
      `)?.eq('id', id)?.single();
    if (productError) throw productError;

    let product = { ...productDataFromDb };
    if (product?.created_by) {
      const { data: sellerAccountData, error: sellerAccountError } = await supabase?.from('seller_accounts')?.select('business_name, is_verified, rating, business_slug')?.eq('user_id', product.created_by)?.maybeSingle();
      if (!sellerAccountError && sellerAccountData) {
        product.sellerBusinessName = sellerAccountData.business_name;
        product.isSellerVerified = sellerAccountData.is_verified;
        product.sellerRating = sellerAccountData.rating;
        product.sellerBusinessSlug = sellerAccountData.business_slug;
      }
    }
    const finalProduct = toCamelCase(product);
    
    // Symmetrical Dynamic Views Aggregation:
    // Fetch views_count from seller_products and sum them up for this product dynamically!
    try {
      const { data: sellerProds } = await supabase
        .from('seller_products')
        .select('views_count')
        .eq('product_id', id)
        .or('isdeleted.is.null,isdeleted.eq.false');
        
      const sellerViews = sellerProds ? sellerProds.reduce((sum, sp) => sum + (Number(sp.views_count) || 0), 0) : 0;
      finalProduct.combinedViews = (Number(productDataFromDb?.views_count) || 0) + sellerViews;
    } catch (e) {
      console.error('Error fetching combined views in getById:', e);
      finalProduct.combinedViews = Number(productDataFromDb?.views_count) || 0;
    }

    if (finalProduct?.gltfFilePath && finalProduct.gltfFilePath !== 'null' && finalProduct.gltfFilePath.trim() !== '') {
      const { data: urlData } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(finalProduct?.gltfFilePath);
      finalProduct.modelUrl = urlData?.publicUrl;
    } else {
      finalProduct.modelUrl = null;
    }
    if (finalProduct?.thumbnailPath) {
      if (finalProduct.thumbnailPath.startsWith('http')) {
        finalProduct.thumbnailUrl = finalProduct.thumbnailPath;
      } else {
        const { data: thumbData } = supabase?.storage?.from('product-thumbnails')?.getPublicUrl(finalProduct.thumbnailPath);
        if (thumbData?.publicUrl) finalProduct.thumbnailUrl = thumbData.publicUrl;
      }
    }
    return finalProduct;
  },

  async getTrending(limit = 6) {
    const { data, error } = await supabase?.from('products')?.select(`
        *,
        component_count,
        upc,
        added_count,
        specs,
        specs,
        specs,
        average_rating,
        total_ratings,
        category:categories!products_category_id_fkey (id, name, slug),
        manufacturer:manufacturers!products_manufacturer_id_fkey (id, name, slug),
        seller_products(views_count)
      `)?.eq('status', 'active')
      ?.order('views_count', { ascending: false, nullsFirst: false })
      ?.order('last_visited_at', { ascending: false, nullsFirst: false })
      ?.limit(limit);
    if (error) throw error;
    const productsWithCategories = data?.map(product => {
      const parentViews = Number(product.views_count) || 0;
      const sellerViews = product.seller_products?.reduce((sum, sp) => sum + (Number(sp.views_count) || 0), 0) || 0;
      const mappedProduct = { 
        ...product, 
        category_name: product?.category?.name || null,
        combined_views: parentViews + sellerViews
      };
      if (product?.gltf_file_path) {
        const { data: urlData } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(product?.gltf_file_path);
        if (urlData?.publicUrl) mappedProduct.modelUrl = urlData?.publicUrl;
      }
      return mappedProduct;
    }) || [];
    return productsWithCategories?.map(toCamelCase) || [];
  },

  async getFeatured(limit = 4) {
    const { data, error } = await supabase?.from('products')?.select(`
        *,
        component_count,
        upc,
        added_count,
        specs,
        specs,
        specs,
        average_rating,
        total_ratings,
        category:categories!products_category_id_fkey (id, name, slug),
        manufacturer:manufacturers!products_manufacturer_id_fkey (id, name, slug)
      `)?.eq('is_featured', true)?.eq('status', 'active')?.order('created_at', { ascending: false })?.limit(limit);
    if (error) throw error;
    const productsWithCategories = data?.map(product => {
      const mappedProduct = { ...product, category_name: product?.category?.name || null };
      if (product?.gltf_file_path) {
        const { data: urlData } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(product?.gltf_file_path);
        if (urlData?.publicUrl) mappedProduct.modelUrl = urlData?.publicUrl;
      }
      return mappedProduct;
    }) || [];
    return productsWithCategories?.map(toCamelCase) || [];
  },

  async getStoreProducts() {
    try {
      const { data, error } = await supabase.rpc('get_public_products');
      if (error) throw new Error(error.message);
      const products = data.map(toCamelCaseSellerProduct);
      return { products, totalCount: products.length, page: 1, itemsPerPage: products.length, totalPages: 1 };
    } catch (error) { throw error; }
  },

  async getPublicCatalogProducts(filters = {}) {
    try {
      let query = supabase.from('products').select(`
          *,
          component_count,
          upc,
          category:categories(id, name, slug, description),
          manufacturer:manufacturers(id, name, slug, logo_url)
        `, { count: 'exact' });

      if (filters?.includeInactive !== true) query = query.eq('status', 'active');
      if (filters?.holiday) {
        const search = `"%${String(filters.holiday).replace(/"/g, '""')}%"`;
        query = query.or(`holiday_setting.ilike.${search},keywords.ilike.${search},name.ilike.${search}`);
      }
      if (filters?.season) {
        const search = `"%${String(filters.season).replace(/"/g, '""')}%"`;
        query = query.or(`seasonal_setting.ilike.${search},keywords.ilike.${search},name.ilike.${search}`);
      }
      if (filters?.searchTerm) {
        const words = String(filters.searchTerm).toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
        for (const word of words) {
           const search = `"%${word.replace(/"/g, '""')}%"`;
           query = query.or(`name.ilike.${search},description.ilike.${search},upc.ilike.${search},sku.ilike.${search}`);
        }
      }
      if (filters?.categoryIds?.length > 0) query = query.in('category_id', filters?.categoryIds);
      if (filters?.manufacturerIds?.length > 0) query = query.in('manufacturer_id', filters?.manufacturerIds);
      if (filters?.priceMin !== undefined) query = query.gte('price', filters.priceMin);
      if (filters?.priceMax !== undefined) query = query.lte('price', filters.priceMax);

      let boostedProducts = [];
      let excludeIds = [];
      const isPageOne = (!filters?.page || filters.page == 1);
      const isDefaultSort = (!filters?.sortBy || filters.sortBy === 'relevance' || filters.sortBy === 'newest');
      const hasCatPrefs = filters?.preferredCategoryNames?.length > 0;
      const hasBrandPrefs = filters?.preferredBrandIds?.length > 0;
      const hasSearch = !!filters?.searchTerm;

      if ((hasCatPrefs || hasBrandPrefs) && isPageOne && isDefaultSort && !hasSearch) {
        let preferredCategoryIds = [];
        if (hasCatPrefs) {
            const { data: allCats } = await supabase.from('categories').select('id, name');
            if (allCats) {
                const normalizedPrefs = filters.preferredCategoryNames.map(p => p.toLowerCase().trim());
                preferredCategoryIds = allCats.filter(c => normalizedPrefs.includes(c.name.toLowerCase().trim())).map(c => c.id);
            }
        }
        const candidateLimit = 40;
        let orConditions = [];
        if (preferredCategoryIds.length > 0) orConditions.push(`category_id.in.(${preferredCategoryIds.join(',')})`);
        if (hasBrandPrefs) orConditions.push(`manufacturer_id.in.(${filters.preferredBrandIds.join(',')})`);
        
        if (orConditions.length > 0) {
            const { data: candidateData } = await supabase.from('products').select(`*, component_count, upc, added_count,
        specs,
        specs, category:categories(id, name, slug, description), manufacturer:manufacturers(id, name, slug, logo_url)`).eq('status', 'active').or(orConditions.join(',')).limit(candidateLimit).order('created_at', { ascending: false });
            if (candidateData && candidateData.length > 0) {
                 const featured = candidateData.filter(p => p.is_featured);
                 const regular = candidateData.filter(p => !p.is_featured);
                 const shuffle = (array) => { for (let i = array.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [array[i], array[j]] = [array[j], array[i]]; } return array; };
                 const finalBoosted = [...shuffle([...featured]), ...shuffle([...regular])].slice(0, 12);
                 boostedProducts = finalBoosted.map(p => ({ ...p, is_boosted: true }));
                 excludeIds = finalBoosted.map(p => p.id);
                 query = query.not('id', 'in', `(${excludeIds.join(',')})`);
            }
        }
      }
      
      switch (filters?.sortBy) {
        case 'price-low': query = query.order('price', { ascending: true }); break;
        case 'price-high': query = query.order('price', { ascending: false }); break;
        case 'name-asc': query = query.order('name', { ascending: true }); break;
        case 'name-desc': query = query.order('name', { ascending: false }); break;
        case 'rating': query = query.order('average_rating', { ascending: false, nullsLast: true }); break;
        case 'oldest': query = query.order('created_at', { ascending: true }); break;
        case 'popularity': query = query.order('sold_count', { ascending: false, nullsFirst: false }).order('views_count', { ascending: false, nullsFirst: false }); break;
        default: query = query.order('created_at', { ascending: false });
      }

      const applyPagination = (q) => {
        const page = filters?.page || 1;
        const itemsPerPage = filters?.itemsPerPage || 24;
        const effectiveItemsPerPage = itemsPerPage - boostedProducts.length;
        const start = (page - 1) * itemsPerPage;
        const end = start + effectiveItemsPerPage - 1;
        return q.range(start, end);
      };

      query = applyPagination(query);
      let { data, error, count } = await query;
      let isPartialMatch = false;
      const words = String(filters?.searchTerm || '').toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);

      if (count === 0 && words.length > 1) {
          let fallbackQuery = supabase.from('products').select(`*, component_count, upc, category:categories(id, name, slug, description), manufacturer:manufacturers(id, name, slug, logo_url)`, { count: 'exact' });
          if (filters?.includeInactive !== true) fallbackQuery = fallbackQuery.eq('status', 'active');
          if (filters?.categoryIds?.length > 0) fallbackQuery = fallbackQuery.in('category_id', filters.categoryIds);
          if (filters?.manufacturerIds?.length > 0) fallbackQuery = fallbackQuery.in('manufacturer_id', filters.manufacturerIds);
          if (filters?.priceMin !== undefined) fallbackQuery = fallbackQuery.gte('price', filters.priceMin);
          if (filters?.priceMax !== undefined) fallbackQuery = fallbackQuery.lte('price', filters.priceMax);
          const orConditions = words.map(w => { const search = `"%${w.replace(/"/g, '""')}%"`; return `name.ilike.${search},description.ilike.${search},upc.ilike.${search},sku.ilike.${search}`; }).join(',');
          fallbackQuery = fallbackQuery.or(orConditions);
          if (excludeIds.length > 0) fallbackQuery = fallbackQuery.not('id', 'in', `(${excludeIds.join(',')})`);
          switch (filters?.sortBy) {
            case 'price-low': fallbackQuery = fallbackQuery.order('price', { ascending: true }); break;
            case 'price-high': fallbackQuery = fallbackQuery.order('price', { ascending: false }); break;
            case 'rating': fallbackQuery = fallbackQuery.order('average_rating', { ascending: false, nullsLast: true }); break;
            default: fallbackQuery = fallbackQuery.order('created_at', { ascending: false });
          }
          fallbackQuery = applyPagination(fallbackQuery);
          const fallbackResult = await fallbackQuery;
          data = fallbackResult.data;
          error = fallbackResult.error;
          count = fallbackResult.count;
          isPartialMatch = true;
      }
      if (error) throw new Error(error.message);
      let combinedData = data;
      if (boostedProducts.length > 0) combinedData = [...boostedProducts, ...data];
      const productsWithUrls = combinedData?.map(product => {
        if (product?.gltf_file_path) {
          const { data: urlData } = supabase?.storage?.from('product-3d-models')?.getPublicUrl(product?.gltf_file_path);
          if (urlData?.publicUrl) product.modelUrl = urlData?.publicUrl;
        }
        if (product?.thumbnail_path) {
          if (product.thumbnail_path.startsWith('http')) {
            product.thumbnailUrl = product.thumbnail_path;
          } else {
            const { data: urlData } = supabase?.storage?.from('product-thumbnails')?.getPublicUrl(product?.thumbnail_path);
            if (urlData?.publicUrl) product.thumbnailUrl = urlData?.publicUrl;
          }
        }
        return product;
      }) || [];
      // Symmetrical Dynamic Views Aggregation:
      // Fetch views_count from seller_products and sum them up for each product dynamically!
      const productIds = productsWithUrls.map(p => p.id).filter(Boolean);
      let sellerViewsMap = {};
      try {
        if (productIds.length > 0) {
          const { data: sellerProds } = await supabase
            .from('seller_products')
            .select('product_id, views_count')
            .in('product_id', productIds)
            .or('isdeleted.is.null,isdeleted.eq.false');

          if (sellerProds) {
            sellerProds.forEach(sp => {
              const pId = sp.product_id;
              const views = Number(sp.views_count) || 0;
              sellerViewsMap[pId] = (sellerViewsMap[pId] || 0) + views;
            });
          }
        }
      } catch (e) {
        console.error('Error fetching combined seller product views in getPublicCatalogProducts:', e);
      }

      const products = productsWithUrls?.map(p => { 
        let camel = toCamelCase(p); 
        const sellerViews = sellerViewsMap[camel.id] || 0;
        // Total Views = product views_count + sum(seller_products views_count)
        camel.combinedViews = (Number(p.views_count) || 0) + sellerViews;
        if (isPartialMatch) camel.isPartialMatch = true; 
        return camel; 
      }) || [];
      const adjustedCount = (count || 0) + boostedProducts.length;
      const pageVal = filters?.page || 1;
      const itemsPerPageVal = filters?.itemsPerPage || 24;
      return { products, totalCount: adjustedCount, page: pageVal, itemsPerPage: itemsPerPageVal, totalPages: Math.ceil(adjustedCount / itemsPerPageVal) };
    } catch (error) { throw error; }
  },

  async searchProducts(searchTerm) {
    if (!searchTerm) return [];
    const { products } = await this.getPublicCatalogProducts({ searchTerm, itemsPerPage: 10 });
    return products;
  },

  async getBySlug(slug) {
    const { data, error } = await supabase?.from('products')?.select(`*, component_count, upc, added_count,
        specs,
        specs, category:categories(id, name, slug), manufacturer:manufacturers(id, name, slug)`)?.eq('slug', slug)?.maybeSingle();
    if (error) throw error;
    if (!data) { const err = new Error('Model not found'); err.code = 'PGRST116'; throw err; }
    return this.getById(data.id);
  },

  mapProductToDb(product) {
    return {
      name: product?.name,
      slug: product?.slug,
      description: product?.description,
      price: product?.price,
      sku: product?.sku,
      stock_quantity: product?.stockQuantity,
      category_id: product?.categoryId,
      manufacturer_id: product?.manufacturerId,
      thumbnail_path: product?.thumbnailPath,
      gltf_file_path: product?.gltfFilePath,
      is_featured: product?.isFeatured,
      is_certified: product?.isCertified,
      staffpick: product?.staffpick,
      is_3d_manufacturing: product?.is3dManufacturing,
      preferred_3d_method: product?.preferred_3d_method,
      is_cnc_capable: product?.isCncCapable,
      cnc_material: product?.cncMaterial,
      cnc_grade: product?.cncGrade,
      image_urls: product?.imageUrls || [],
      status: product?.status || 'active'
    };
  }
};